import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { getTenantClusterByTenant, setTenantClusterForTenant } from '@/utils/tenantClusterStorage';
import { _getStorage, _setStorage } from '@/utils/useLocalStorage';

// 状态接口定义
export interface GlobalState {
  // 用户信息
  userInfo: any;
  setUserInfo: (userInfo: any) => void;

  // 配置信息
  config: any;
  setConfig: (config: any) => void;

  // 权限信息
  auth: any;
  setAuth: (auth: any) => void;

  // 管理员状态
  isAdmin: boolean;
  setIsAdmin: (isAdmin: boolean) => void;

  // 后台菜单
  backMenu: string[];
  setBackMenu: (backMenu: string[]) => void;

  // 集群列表
  clusterList: any[];
  setClusterList: (clusterList: any[]) => void;

  // 租户列表
  tenantList: any[];
  setTenantList: (tenantList: any[]) => void;

  // 模式
  mode: 'system' | 'tenant' | 'data-center' | '' | undefined;
  setMode: (mode: GlobalState['mode']) => void;

  // 当前集群
  cluster: string;
  setCluster: (cluster: string) => void;

  // 当前租户
  tenant: string;
  setTenant: (tenant: string) => void;

  // 租户下当前集群
  tenantCluster: string;
  setTenantCluster: (tenantCluster: string) => void;

  // 租户下关联的集群列表
  tenantClusters: any[];
  setTenantClusters: (tenantClusters: any[]) => void;

  // 全局加载状态
  loading: boolean;
  setLoading: (loading: boolean) => void;

  // 登录状态
  isLogin: boolean;
  setIsLogin: (isLogin: boolean) => void;
}

// 创建store
export const useGlobalStore = create<GlobalState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      // 初始状态
      userInfo: _getStorage('userInfo', {}),
      config: _getStorage('config', {}),
      auth: _getStorage('auth', null),
      isAdmin: _getStorage('isAdmin', false),
      backMenu: _getStorage('backMenu', []),
      clusterList: _getStorage('clusterList', []),
      tenantList: _getStorage('tenantList', []),
      mode: _getStorage('mode', 'data-center'),
      cluster: _getStorage('cluster', ''),
      tenant: _getStorage('tenant', ''),
      tenantCluster: getTenantClusterByTenant(_getStorage('tenant', '')) || '',
      tenantClusters: _getStorage('tenantClusters', []),
      loading: true,
      isLogin: _getStorage('isLogin', false),

      // 用户信息操作
      setUserInfo: (userInfo) => {
        set((state) => {
          state.userInfo = userInfo;
        });
        _setStorage('userInfo', JSON.stringify(userInfo));
      },

      // 全局配置操作
      setConfig: (config) => {
        set((state) => {
          state.config = config;
        });
        _setStorage('config', JSON.stringify(config));
      },

      // 权限信息操作
      setAuth: (auth) => {
        set((state) => {
          state.auth = auth;
        });
        _setStorage('auth', JSON.stringify(auth));
      },

      // 管理员状态操作
      setIsAdmin: (isAdmin) => {
        set((state) => {
          state.isAdmin = isAdmin;
        });
        _setStorage('isAdmin', JSON.stringify(isAdmin));
      },

      // 菜单操作
      setBackMenu: (backMenu) => {
        set((state) => {
          state.backMenu = backMenu;
        });
        _setStorage('backMenu', JSON.stringify(backMenu));
      },

      // 集群列表操作
      setClusterList: (clusterList) => {
        set((state) => {
          state.clusterList = clusterList;
        });
        _setStorage('clusterList', JSON.stringify(clusterList));
      },

      // 租户列表操作
      setTenantList: (tenantList) => {
        set((state) => {
          state.tenantList = tenantList;
        });
        _setStorage('tenantList', JSON.stringify(tenantList));
      },

      // 模式操作
      setMode: (mode) => {
        set((state) => {
          state.mode = mode;
        });
        _setStorage('mode', mode);
      },

      // 集群选择操作
      setCluster: (cluster) => {
        set((state) => {
          state.cluster = cluster;
        });
        _setStorage('cluster', cluster);
      },

      // 租户选择操作
      setTenant: (tenant) => {
        set((state) => {
          state.tenant = tenant;
          state.tenantCluster = getTenantClusterByTenant(tenant) || '';
        });
        _setStorage('tenant', tenant);
      },

      // 租户集群操作
      setTenantCluster: (tenantCluster) => {
        set((state) => {
          state.tenantCluster = tenantCluster;
        });
        setTenantClusterForTenant(get().tenant, tenantCluster);
      },

      // 租户集群列表操作
      setTenantClusters: (tenantClusters) => {
        set((state) => {
          state.tenantClusters = tenantClusters;
        });
        _setStorage('tenantClusters', JSON.stringify(tenantClusters));
      },

      // 加载状态操作
      setLoading: (loading) => {
        set((state) => {
          state.loading = loading;
        });
      },

      // 登录状态操作
      setIsLogin: (isLogin) => {
        set((state) => {
          state.isLogin = isLogin;
        });
        _setStorage('isLogin', JSON.stringify(isLogin));
      },

      // 重置状态
      reset: () => {
        set((state) => {
          state.userInfo = {};
          state.config = {};
          state.auth = null;
          state.isAdmin = false;
          state.backMenu = [];
          state.clusterList = [];
          state.tenantList = [];
          state.mode = 'data-center';
          state.cluster = '';
          state.tenant = '';
          state.tenantCluster = '';
          state.tenantClusters = [];
          state.loading = false;
          state.isLogin = false;
        });
      },
    })),
  ),
);
export const useUserInfo = () => useGlobalStore((state) => ({ userInfo: state.userInfo, setUserInfo: state.setUserInfo }));
export const useConfig = () => useGlobalStore((state) => ({ config: state.config, setConfig: state.setConfig }));
export const useAuth = () => useGlobalStore((state) => ({ auth: state.auth, setAuth: state.setAuth }));
export const useAdmin = () => useGlobalStore((state) => ({ isAdmin: state.isAdmin, setIsAdmin: state.setIsAdmin }));
export const useBackMenu = () => useGlobalStore((state) => ({ backMenu: state.backMenu, setBackMenu: state.setBackMenu }));
export const useClusterList = () =>
  useGlobalStore((state) => ({
    clusterList: state.clusterList,
    setClusterList: state.setClusterList,
  }));
export const useTenantList = () => useGlobalStore((state) => ({ tenantList: state.tenantList, setTenantList: state.setTenantList }));
export const useMode = () => useGlobalStore((state) => ({ mode: state.mode, setMode: state.setMode }));
export const useCluster = () => useGlobalStore((state) => ({ cluster: state.cluster, setCluster: state.setCluster }));
export const useTenant = () =>
  useGlobalStore((state) => ({
    tenant: state.tenant,
    setTenant: state.setTenant,
    getCurrTenant: (t) => state.tenantList?.find((i) => i.name === t),
    cluster: state.tenantCluster,
    clusterList: state.tenantClusters,
    setTenantCluster: (c) => {
      state.setTenantCluster(c);
    },
  }));
export const useTenantCluster = () =>
  useGlobalStore((state) => ({
    tenantCluster: state.tenantCluster,
    setTenantCluster: state.setTenantCluster,
  }));
export const useTenantClusters = () =>
  useGlobalStore((state) => ({
    tenantClusters: state.tenantClusters,
    setTenantClusters: state.setTenantClusters,
  }));
export const useLoading = () => useGlobalStore((state) => ({ loading: state.loading, setLoading: state.setLoading }));
